import { type FC } from 'react';
import { useFormik } from 'formik';
import { Button, Input, Spinner, useAlert } from '@selfinspectionorg/ui-kit';
import colors from '@selfinspectionorg/ui-kit/css/colors.module.scss';
import type { KeyedMutator } from 'swr';
import type { IUserProfileView } from '@shared/services';
import { normalizePhone, validationSchema, type ProfileFormValues } from './form';
import styles from './GeneralTab.module.scss';
import { updateUserProfile } from '@/shared/api/user';
import { getApiErrorString } from '@/shared/utils';

interface Props {
  profile: IUserProfileView | undefined;
  isLoading: boolean;
  error: unknown;
  /** Revalidates the shared profile cache — used by the error-state retry and after a successful save. */
  mutate: KeyedMutator<IUserProfileView>;
}

/**
 * The General tab: view and edit the account's profile. First/last name and phone are editable; email
 * and location are read-only. The form initializes from the shared `useUserProfile` query (owned by
 * `ProfileView` and passed down) with `enableReinitialize`, so a background revalidation refreshes
 * pristine fields and a successful save resets the dirty state. Save is disabled until the form has
 * valid unsaved edits.
 */
export const GeneralTab: FC<Props> = ({ profile, isLoading, error, mutate }) => {
  const alert = useAlert();

  const form = useFormik<ProfileFormValues>({
    enableReinitialize: true,
    initialValues: {
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      // Legacy rows may hold a `(123) 456-7890` phone or none at all; normalize to E.164 on open so
      // the value is valid, displays correctly in the phone input, and doesn't mark the form dirty.
      phone: normalizePhone(profile?.phone),
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await updateUserProfile({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          phone: values.phone,
        });
      } catch (caught) {
        // Keep the entered values in place so the user can retry.
        alert.error("Couldn't update profile", getApiErrorString(caught), {
          showCloseButton: true,
          showIcon: true,
          testId: 'profile-save-error',
        });

        return;
      }

      // The PUT succeeded — confirm it before revalidating so a failed follow-up refresh can't turn a
      // successful save into an error message.
      alert.success('Profile updated', undefined, {
        showCloseButton: true,
        showIcon: true,
        testId: 'profile-save-success',
      });

      // Revalidate so the reinitialized form reflects the saved values and drops its dirty state. A
      // failure here only leaves the form dirty; it does not undo the save, so swallow it rather than
      // letting it reject the submit handler or reach the update-error path above.
      await mutate().catch(() => undefined);
    },
  });

  if (isLoading) {
    return (
      <div className={styles.state} data-testid="profile-loading">
        <Spinner color={colors.selfInspection_600} width={40} height={40} />
      </div>
    );
  }

  // Only show the error state on a genuine load failure (no data yet). SWR keeps the last data and sets
  // `error` when a background revalidation — e.g. on window focus — fails; swapping in the error state
  // then would discard the user's unsaved edits.
  if (error && !profile) {
    return (
      <div className={styles.state} data-testid="profile-error">
        <p className={styles.stateText}>We couldn&apos;t load your profile.</p>
        <Button onClick={() => mutate()} data-testid="profile-retry-button">
          Try again
        </Button>
      </div>
    );
  }

  const isSaveDisabled = !form.dirty || !form.isValid || form.isSubmitting;

  return (
    <form className={styles.form} onSubmit={form.handleSubmit}>
      <div className={styles.grid}>
        <Input
          name="firstName"
          label="First name"
          placeholder="Enter first name"
          value={form.values.firstName}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          errored={!!form.touched.firstName && !!form.errors.firstName}
          helperMessage={form.touched.firstName ? form.errors.firstName : undefined}
          required
          testId="profile-first-name-input"
        />
        <Input
          name="lastName"
          label="Last name"
          placeholder="Enter last name"
          value={form.values.lastName}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          errored={!!form.touched.lastName && !!form.errors.lastName}
          helperMessage={form.touched.lastName ? form.errors.lastName : undefined}
          required
          testId="profile-last-name-input"
        />
        <Input name="email" label="Email" value={profile?.email ?? ''} disabled testId="profile-email-input" />
        <Input.Phone
          name="phone"
          label="Phone"
          placeholder="Enter phone"
          value={form.values.phone}
          onChange={(value) => form.setFieldValue('phone', value)}
          onBlur={() => form.setFieldTouched('phone', true)}
          // Surface the phone error once it's been touched OR the form is dirty. A legacy/empty phone
          // makes Save invalid without the user touching the field, so editing another field (dirty)
          // should reveal why Save stays disabled.
          errored={(!!form.touched.phone || form.dirty) && !!form.errors.phone}
          helperMessage={form.touched.phone || form.dirty ? form.errors.phone : undefined}
          required
          testId="profile-phone-input"
        />
        <Input
          name="location"
          label="Location"
          wrapperClassName={styles.fullWidth}
          value={profile?.location ?? ''}
          disabled
          testId="profile-location-input"
        />
      </div>

      <div className={styles.footer}>
        <Button
          type="submit"
          width="100%"
          isLoading={form.isSubmitting}
          disabled={isSaveDisabled}
          data-testid="profile-save-button"
        >
          Save
        </Button>
      </div>
    </form>
  );
};
